export type FlowNodeType =
  | 'start'
  | 'phase'
  | 'group'
  | 'condition'
  | 'action'
  | 'end'
  | 'macro-start'
  | 'macro-end'
  | 'macro-call'

export type FlowGraphNodeV1 = {
  id: string
  type: FlowNodeType
  label: string
  locked?: boolean
  data?: Record<string, unknown>
  position?: { x: number; y: number }
}

export type FlowGraphEdgeV1 = {
  id: string
  fromNodeId: string
  fromPort: string
  toNodeId: string
  toPort: string
}

export type FlowGraphV1 = {
  version: 1
  nodes: FlowGraphNodeV1[]
  edges: FlowGraphEdgeV1[]
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v)

export const coerceFlowGraphV1 = (value: unknown): FlowGraphV1 | null => {
  if (!isRecord(value)) return null
  if (value.version !== 1) return null

  const nodesRaw = value.nodes
  const edgesRaw = value.edges
  if (!Array.isArray(nodesRaw) || !Array.isArray(edgesRaw)) return null

  const nodes: FlowGraphNodeV1[] = []
  for (const item of nodesRaw) {
    if (!isRecord(item)) return null
    const id = typeof item.id === 'string' ? item.id : ''
    const type = typeof item.type === 'string' ? item.type : ''
    const label = typeof item.label === 'string' ? item.label : ''
    if (!id || !label) return null
    if (
      type !== 'start' &&
      type !== 'phase' &&
      type !== 'group' &&
      type !== 'condition' &&
      type !== 'action' &&
      type !== 'end' &&
      type !== 'macro-start' &&
      type !== 'macro-end' &&
      type !== 'macro-call'
    ) {
      return null
    }

    const locked = typeof item.locked === 'boolean' ? item.locked : undefined
    const data = isRecord(item.data) ? (item.data as Record<string, unknown>) : undefined

    const pos = isRecord(item.position) ? item.position : null
    const x = pos && typeof pos.x === 'number' && Number.isFinite(pos.x) ? pos.x : undefined
    const y = pos && typeof pos.y === 'number' && Number.isFinite(pos.y) ? pos.y : undefined
    const position = x === undefined || y === undefined ? undefined : { x, y }

    nodes.push({ id, type: type as FlowNodeType, label, locked, data, position })
  }

  const edges: FlowGraphEdgeV1[] = []
  for (const item of edgesRaw) {
    if (!isRecord(item)) return null
    const id = typeof item.id === 'string' ? item.id : ''
    const fromNodeId = typeof item.fromNodeId === 'string' ? item.fromNodeId : ''
    const fromPort = typeof item.fromPort === 'string' ? item.fromPort : ''
    const toNodeId = typeof item.toNodeId === 'string' ? item.toNodeId : ''
    const toPort = typeof item.toPort === 'string' ? item.toPort : ''
    if (!id || !fromNodeId || !fromPort || !toNodeId || !toPort) return null
    edges.push({ id, fromNodeId, fromPort, toNodeId, toPort })
  }

  return { version: 1, nodes, edges }
}

export type FlowchartStep = {
  phaseId?: string
  phaseMode?: string
  groupId?: string
  /** If true, card clicks do NOT auto-advance; requires checkbox interaction instead. */
  noAutoAdvance?: boolean
  /** Pre-check this many checkboxes on every card when entering the group (level mode). */
  checkboxPreset?: number
}

const getString = (obj: Record<string, unknown> | undefined, key: string): string | undefined => {
  const v = obj?.[key]
  if (typeof v !== 'string') return undefined
  const s = v.trim()
  return s ? s : undefined
}

export const buildFlowchartSteps = (graph: FlowGraphV1): FlowchartStep[] => {
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]))

  const outgoing = new Map<string, Map<string, FlowGraphEdgeV1[]>>()
  for (const edge of graph.edges) {
    let byPort = outgoing.get(edge.fromNodeId)
    if (!byPort) {
      byPort = new Map()
      outgoing.set(edge.fromNodeId, byPort)
    }
    const list = byPort.get(edge.fromPort) ?? []
    list.push(edge)
    byPort.set(edge.fromPort, list)
  }

  const pick = (fromNodeId: string, preferredPorts: string[]): FlowGraphEdgeV1 | null => {
    const byPort = outgoing.get(fromNodeId)
    if (!byPort) return null

    for (const port of preferredPorts) {
      const list = byPort.get(port)
      if (Array.isArray(list) && list.length) return list[0]!
    }

    // Fallback: any outgoing edge.
    for (const list of byPort.values()) {
      if (Array.isArray(list) && list.length) return list[0]!
    }

    return null
  }

  const start =
    graph.nodes.find((n) => n.type === 'start') ??
    graph.nodes.find((n) => n.id === 'node-start') ??
    null
  if (!start) return []

  let cursor = pick(start.id, ['next'])?.toNodeId ?? null

  const steps: FlowchartStep[] = []
  let current: FlowchartStep | null = null

  const visited = new Set<string>()
  let guard = 0

  while (cursor && guard++ < 500) {
    const visitKey = cursor
    if (visited.has(visitKey)) break
    visited.add(visitKey)

    const node = nodeById.get(cursor)
    if (!node) break

    if (node.type === 'phase') {
      if (current) steps.push(current)
      current = {
        phaseId: getString(node.data, 'phaseId'),
        phaseMode: getString(node.data, 'phaseMode'),
        noAutoAdvance: node.data?.noAutoAdvance === true,
      }
    }

    if (node.type === 'group') {
      if (!current) current = {}
      current.groupId = getString(node.data, 'groupId') ?? current.groupId
      if (node.data?.noAutoAdvance === true) current.noAutoAdvance = true
    }

    if (node.type === 'action' && node.data?.actionType === 'set-initial-checkboxes') {
      if (!current) current = {}
      const count = node.data.checkboxCount
      if (typeof count === 'number' && count > 0) current.checkboxPreset = count
    }

    if (node.type === 'end') break

    const nextEdge =
      node.type === 'condition'
        ? pick(node.id, ['true', 'next', 'else'])
        : pick(node.id, ['next', 'true'])

    cursor = nextEdge?.toNodeId ?? null
  }

  if (current) steps.push(current)

  // If the graph has no explicit phases, treat it as a single-step flow.
  if (!steps.length) {
    const group = graph.nodes.find((n) => n.type === 'group')
    const groupId = group ? getString(group.data, 'groupId') : undefined
    return [{ groupId }]
  }

  return steps
}
